/* eslint-disable @next/next/no-img-element */
import Image from 'next/image';
import Link from 'next/link';
import React, { useMemo } from 'react';
import SpotlightHeroItem from '../SpotlightHero/item';
import { CompetitorComparisonBox } from '@/components/Common/LandingPage';
import { LandingPageData } from '@/shared/types/landing-page';
import { RightOutlined } from '../../Icons';
import { useConfigProvider } from '@/lib/context';
import './index.less';

export default function LandingPageHero({
  data,
  isBuilderIOPage,
  isFindingClothingManufacturers,
  onClick,
}: {
  data: LandingPageData;
  isBuilderIOPage?: boolean;
  isFindingClothingManufacturers?: boolean;
  onClick?: () => void;
}) {
  const { isMobile } = useConfigProvider();

  const pageHeroStyle = useMemo(() => {
    if (isBuilderIOPage && !isFindingClothingManufacturers) {
      return {
        background: '#fff',
        marginTop: 60,
      };
    }
    return null;
  }, [isBuilderIOPage, isFindingClothingManufacturers]);

  const signUpButtonStyle = useMemo(() => {
    if (isBuilderIOPage && !isFindingClothingManufacturers) {
      return {
        borderRadius: 8,
        padding: '12px 20px',
      };
    }
    return null;
  }, [isBuilderIOPage, isFindingClothingManufacturers]);

  const buttonText = useMemo(() => {
    if (isMobile && data.hero.button?.mobileText) {
      return data.hero.button.mobileText;
    }
    return data.hero.button.text;
  }, [data.hero.button.mobileText, data.hero.button.text, isMobile]);

  return (
    <div className="landing-page-hero" style={pageHeroStyle}>
      <div className="header">
        {data.hero?.showPietraLogo && (
          <Link href="https://www.pietrastudio.com" target="_blank" className="logo">
            <Image
              src="/images/logos/pietra-logo.svg"
              width={130}
              height={34}
              alt="Pietra"
              title="Pietra"
              loading="eager"
            />
          </Link>
        )}
        <h1 className="title">
          {data.hero.title.map((text) => (
            <span key={text}>{text}</span>
          ))}
        </h1>
        {data.hero?.subtitle && <span className="subtitle">{data.hero.subtitle}</span>}
        {buttonText && (
          <div className="button-container">
            <div className="signup-button" onClick={onClick} style={signUpButtonStyle}>
              {buttonText.map((item) => (
                <span className={item.style} key={item.text}>
                  {item.text}
                </span>
              ))}
              {!data.hero?.hideArrowRightIcon && (
                <Image
                  width={12}
                  height={12}
                  src="/images/homepage-v2/hero-with-brand-video-bg/icon-arrow-right.png"
                  alt="right arrow"
                />
              )}
            </div>
          </div>
        )}
      </div>
      {data.hero?.spotlightItems && (
        <SpotlightHeroItem
          isBuilderIOPage={isBuilderIOPage}
          spotlightItems={data.hero?.spotlightItems}
          textColor="#000"
        />
      )}
      {data.hero?.rating && (
        <div className="rate-list">
          <div className="star">
            <Image
              width={121}
              height={24}
              src="/images/homepage-v2/hero-with-brand-video-bg/icon-stars.png"
              alt="4.5 Stars Rating"
            />
            <div className="star-score">4.5</div>
          </div>
          <div className="trustpilot">
            <Image
              width={65}
              height={16}
              src="/images/homepage-v2/hero-with-brand-video-bg/logo-trustpilot.png"
              alt="Trustpilot Logo"
            />
          </div>
        </div>
      )}
      {data.hero.competitorComparisons && (
        <div className="comparisons">
          {data.hero.competitorComparisons.map((comparison) => (
            <CompetitorComparisonBox comparison={comparison} key={comparison.competitor.name} />
          ))}
        </div>
      )}
      {data.hero?.showBrandLogo && (
        <div className="logos-container">
          {[
            { name: 'Tech Crunch', src: 'tech-crunch.svg' },
            { name: 'US Weekly', src: 'us-weekly.svg' },
            { name: 'The Information', src: 'the-information.svg' },
            { name: 'Business Insider', src: 'business-insider.svg' },
            { name: 'Retail Brew', src: 'retail-brew.png' },
            { name: 'Forbes', src: 'forbes.svg' },
          ].map((img) => (
            <div className="publication-logo" key={img.name}>
              <Image src={`/images/logos/${img.src}`} alt={img.name} loading="eager" fill />
            </div>
          ))}
        </div>
      )}
      {data.hero?.showBrandLogo && <div className="divider" />}
      {data.hero.quote && (
        <div className="quote-container">
          <span className="quote">{data.hero.quote.quote}</span>
          <Link href={data.hero.quote.link} target="_blank" className="author">
            {data.hero.quote.author} <RightOutlined />
          </Link>
        </div>
      )}
      {data.hero?.showPlatformLogo && (
        <div className="paired-tag">
          <span>Works with proven platforms</span>
          <div className="logos-container">
            <img src="/svg/shopify-wordmark.svg" alt="Shopify" />
            <img src="/svg/tiktok-shop-wordmark.svg" alt="Tiktok" />
            <img src="/svg/amazon-wordmark.svg" alt="Amazon" />
            <img src="/svg/wix-wordmark.svg" alt="Amazon" />
            <img src="/svg/woo-wordmark.svg" alt="Amazon" />
            <img src="/svg/klaviyo-wordmark.svg" alt="Amazon" />
          </div>
        </div>
      )}
    </div>
  );
}
